import MapSettings from '@rio-cloud/rio-uikit/components/map/components/features/MapSettings';
import MapTypeSettings from '@rio-cloud/rio-uikit/components/map/components/features/settings/builtinSettings/MapTypeSettings';
import EventUtils from '@rio-cloud/rio-uikit/EventUtils';
import HereMap from '@rio-cloud/rio-uikit/Map';
import Marker from '@rio-cloud/rio-uikit/Marker';
import type { MapApi, MapEvent, Position } from '@rio-cloud/rio-uikit/mapTypes';
import SingleMapMarker from '@rio-cloud/rio-uikit/SingleMapMarker';
import { useRef } from 'react';
import { useIntl } from 'react-intl';

import { config } from '../../config.ts';

type DraggableLocationMapProps = {
    mapPosition?: Position;
    addressPosition?: Position;
    onPositionChange?: (position: Position) => void;
    showMarker?: boolean;
};

const initialPosition: Position = { lat: 51.1657, lng: 10.4515 };

// see https://uikit.developers.rio.cloud/#components/mapDraggableMarker
export const DraggableLocationMap = ({
    mapPosition,
    addressPosition,
    onPositionChange,
    showMarker,
}: DraggableLocationMapProps) => {
    const mapApiRef = useRef<MapApi>();
    const intl = useIntl();

    if (!config.hereApiKey) {
        return <div>HERE API key is not configured.</div>;
    }

    const updatePosition = (position: Position) => {
        onPositionChange?.(position);
    };

    const mapEventListenerMap = {
        [EventUtils.TAP]: (event: MapEvent) => {
            const pointer = event.currentPointer;
            updatePosition(mapApiRef.current?.map.screenToGeo(pointer.viewportX, pointer.viewportY));
        },
    };

    const markerEventListenerMap = {
        [EventUtils.DRAG_START]: (event: MapEvent) => {
            // disable the default draggability of the underlying map
            // and calculate the offset between mouse and target's position
            // when starting to drag a marker object:
            const target = event.target;
            const pointer = event.currentPointer;

            if (target.draggable) {
                const targetPosition = mapApiRef.current?.map.geoToScreen(target.getGeometry());
                target.offset = {
                    x: pointer.viewportX - targetPosition.x,
                    y: pointer.viewportY - targetPosition.y,
                };
                mapApiRef.current?.behavior.disable();
            }
        },
        [EventUtils.DRAG_END]: (event: MapEvent) => {
            // re-enable the default draggability of the underlying map
            // when dragging has completed
            const target = event.target;
            if (target.draggable) {
                mapApiRef.current?.behavior.enable();
            }

            // Update final map marker position
            const pointer = event.currentPointer;
            updatePosition(
                mapApiRef.current?.map.screenToGeo(
                    pointer.viewportX - target.offset.x,
                    pointer.viewportY - target.offset.y
                )
            );
        },
        [EventUtils.DRAG]: (event: MapEvent) => {
            // Listen to the drag event and move the position of the marker
            // as necessary
            const target = event.target;
            const pointer = event.currentPointer;
            if (target.draggable) {
                target.setGeometry(
                    mapApiRef.current?.map.screenToGeo(
                        pointer.viewportX - target.offset.x,
                        pointer.viewportY - target.offset.y
                    )
                );
            }
        },
    };

    const getBoundingBox = () => {
        // Padding decreases as zoom increases (higher zoom = closer in)
        const basePadding = 0.01;
        const minPadding = 0.001;
        const zoom = !addressPosition && !showMarker ? 0.01 : 16; // Default zoom, or get from props/state if dynamic
        const padding = Math.max(basePadding / zoom ** 1.5, minPadding);

        if (showMarker && mapPosition && addressPosition) {
            // Calculate max distance from addressPosition to mapPosition
            const latDiff = Math.abs(addressPosition.lat - mapPosition.lat);
            const lngDiff = Math.abs(addressPosition.lng - mapPosition.lng);
            const maxDistance = Math.max(latDiff, lngDiff);

            // Expand equally around addressPosition so it's centered
            return calculateBoundingBox(addressPosition.lat, addressPosition.lng, padding + maxDistance);
        }

        if (showMarker && mapPosition) {
            return calculateBoundingBox(mapPosition.lat, mapPosition.lng, padding);
        }

        return calculateBoundingBox(
            addressPosition?.lat ?? initialPosition.lat,
            addressPosition?.lng ?? initialPosition.lng,
            padding
        );
    };

    const calculateBoundingBox = (latitude: number, longitude: number, padding: number) => ({
        top: latitude + padding,
        bottom: latitude - padding,
        left: longitude - padding,
        right: longitude + padding,
    });

    return (
        <HereMap
            credentials={{ apikey: config.hereApiKey }}
            boundingBox={getBoundingBox()}
            eventListenerMap={mapEventListenerMap}
            mapSettings={
                <MapSettings
                    options={[
                        <MapTypeSettings
                            key='mapTypeSettings'
                            tooltip={intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.map-type.tooltip',
                            })}
                            dropdownHeaderText={intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.map-type.dropdown-header',
                            })}
                            defaultTypeLabel={intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.map-type.type.default',
                            })}
                            truckTypeLabel={intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.map-type.type.truck',
                            })}
                            terrainTypeLabel={intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.map-type.type.terrain',
                            })}
                            satelliteTypeLabel={intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.map-type.type.staellite',
                            })}
                            nightTypeLabel={intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.map-type.type.night',
                            })}
                        />,
                    ]}
                />
            }
        >
            {(api: MapApi) => {
                mapApiRef.current = api;
                return (
                    <>
                        {showMarker && mapPosition && (
                            <Marker
                                position={mapPosition}
                                icon={<SingleMapMarker iconNames={['delivery']} markerColor='bg-map-marker-poi' />}
                                eventListenerMap={markerEventListenerMap}
                                draggable
                            />
                        )}
                        {addressPosition && (
                            <Marker
                                position={addressPosition}
                                icon={<SingleMapMarker iconNames={['building']} markerColor='bg-map-marker-poi' />}
                                eventListenerMap={markerEventListenerMap}
                            />
                        )}
                    </>
                );
            }}
        </HereMap>
    );
};
